import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getCalendarAvailablePlanCounts } from '@/api/planner';
import {
  clearCustomerInfoCookie,
  loadCustomerInfoFromCookie,
  normalizePhoneNumber,
  saveCustomerInfoToCookie,
} from '@/utils/customerInfoCookie';

type AvailabilityMap = Record<string, number>;

type FetchMonthlyAvailabilityPayload = {
  month: string;
  tourSize: string;
  selectedShopIds: number[] | string[];
  fixedShopIds: number[];
};

type AvailabilityState = {
  dailyAvailCountMap: AvailabilityMap;
  loading: boolean;
  error: string | null;
  customerName: string;
  customerPhone: string;
  isCustomerInfoSet: boolean;
  hasCheckedCustomerInfoRestore: boolean;
};

const initialState: AvailabilityState = {
  dailyAvailCountMap: {},
  loading: false,
  error: null,
  customerName: '',
  customerPhone: '',
  isCustomerInfoSet: false,
  hasCheckedCustomerInfoRestore: false,
};

export const fetchMonthlyAvailability = createAsyncThunk<
  AvailabilityMap,
  FetchMonthlyAvailabilityPayload,
  { rejectValue: string }
>(
  'availability/fetchMonthlyAvailability',
  async (params, thunkAPI) => {
    try {
      const result = await getCalendarAvailablePlanCounts(params);
      return result?.days || {};
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error?.response?.data?.message ||
          error?.message ||
          '월별 가능 일정 조회에 실패했습니다.'
      );
    }
  }
);

const availabilitySlice = createSlice({
  name: 'availability',
  initialState,
  reducers: {
    clearAvailability(state) {
      state.dailyAvailCountMap = {};
      state.loading = false;
      state.error = null;
    },

    setCustomerInfo(state, action: PayloadAction<{ name: string; phone: string }>) {
      const name = String(action.payload?.name || '').trim();
      const phone = normalizePhoneNumber(action.payload?.phone || '');

      state.customerName = name;
      state.customerPhone = phone;
      state.isCustomerInfoSet = !!name && !!phone;
      state.hasCheckedCustomerInfoRestore = true;

      saveCustomerInfoToCookie({
        name,
        phone,
      });
    },

    confirmCustomerInfo(state) {
      state.isCustomerInfoSet = true;
    },

    restoreCustomerInfoFromCookie(state) {
      const customerInfo = loadCustomerInfoFromCookie();

      if (!customerInfo) {
        state.customerName = '';
        state.customerPhone = '';
        state.isCustomerInfoSet = false;
        state.hasCheckedCustomerInfoRestore = true;
        return;
      }

      state.customerName = String(customerInfo.name || '').trim();
      state.customerPhone = normalizePhoneNumber(customerInfo.phone || '');
      state.isCustomerInfoSet = !!state.customerName && !!state.customerPhone;
      state.hasCheckedCustomerInfoRestore = true;
    },

    clearCustomerInfo(state) {
      state.customerName = '';
      state.customerPhone = '';
      state.isCustomerInfoSet = false;
      state.hasCheckedCustomerInfoRestore = true;

      clearCustomerInfoCookie();
    },

    markCustomerInfoRestoreChecked(state) {
      state.hasCheckedCustomerInfoRestore = true;
    },

    resetCustomerInfoRestoreChecked(state) {
      state.hasCheckedCustomerInfoRestore = false;
    },

    setAvailability(state, action: PayloadAction<AvailabilityMap>) {
      state.dailyAvailCountMap = action.payload;
    },

    setTargetDateAvailability(state, action: PayloadAction<{ date: string; count: number }>) {
      const { date, count } = action.payload;
      state.dailyAvailCountMap[date] = count;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMonthlyAvailability.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMonthlyAvailability.fulfilled, (state, action) => {
        state.loading = false;
        state.dailyAvailCountMap = {
          ...state.dailyAvailCountMap,
          ...(action.payload || {}),
        };
      })
      .addCase(fetchMonthlyAvailability.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || '월별 가능 일정 조회에 실패했습니다.';
      });
  },
});

export const {
  clearAvailability,
  setAvailability,
  setTargetDateAvailability,
  clearCustomerInfo,
  setCustomerInfo,
  confirmCustomerInfo,
  restoreCustomerInfoFromCookie,
  markCustomerInfoRestoreChecked,
  resetCustomerInfoRestoreChecked,
} = availabilitySlice.actions;

export default availabilitySlice.reducer;